import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { PLANTILLAS_DE_PUX, IDIOMAS, categoriaQueLimita } from './plantillasDePux';

const STATUS_META = {
  APPROVED:        { color: '#16a34a', dot: '🟢' },
  PENDING:         { color: '#ca8a04', dot: '🟡' },
  REJECTED:        { color: '#dc2626', dot: '🔴' },
  PAUSED:          { color: '#71717a', dot: '⚪' },
  DISABLED:        { color: '#71717a', dot: '⚪' },
  FLAGGED:         { color: '#ea580c', dot: '🟠' },
  NOT_PROVISIONED: { color: '#71717a', dot: '⚪' },
};

const LOCALE_BY_LANG = { es: 'es-CL', en: 'en-US', pt: 'pt-BR' };

function relativeTime(date, lang) {
  if (!date) return null;
  try {
    const d = date?.toDate ? date.toDate() : new Date(date);
    const diffMs = Date.now() - d.getTime();
    const sec = Math.round(diffMs / 1000);
    const min = Math.round(sec / 60);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);
    const rtf = new Intl.RelativeTimeFormat(LOCALE_BY_LANG[lang] || 'es-CL', { numeric: 'auto' });
    if (Math.abs(sec) < 60) return rtf.format(-sec, 'second');
    if (Math.abs(min) < 60) return rtf.format(-min, 'minute');
    if (Math.abs(hr) < 24) return rtf.format(-hr, 'hour');
    return rtf.format(-day, 'day');
  } catch {
    return null;
  }
}

/**
 * Appointment templates panel for bot config → Integrations tab.
 *
 * Only rendered for bots connected via WhatsApp Cloud API (numeroId starts
 * with "wa_api_"). Subscribes to the numero doc for real-time status updates
 * driven by the message_template_status_update webhook.
 *
 * Props:
 *   botId          - current bot id (passed straight to the callable)
 *   numeroId       - bot's assigned Cloud API numero id (e.g., "wa_api_XXX")
 *   organizationId - org id if in org mode, else null
 *   uid            - user id for personal mode (used by callable auth)
 */
export default function AppointmentTemplatesPanel({ botId, numeroId, organizationId, uid }) {
  const { t, i18n } = useTranslation('channels');
  const [numero, setNumero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const lang = (i18n.language || 'es').slice(0, 2);

  const isCloudApi = useMemo(
    () => Boolean(numeroId) && String(numeroId).startsWith('wa_api_'),
    [numeroId]
  );

  // Subscribe to the numero doc so the webhook handler updates reflect live.
  useEffect(() => {
    if (!isCloudApi || !numeroId) {
      setNumero(null);
      setLoading(false);
      return undefined;
    }
    const basePath = organizationId
      ? ['organizations', organizationId, 'numeros', numeroId]
      : ['clientes', uid, 'numeros', numeroId];
    const ref = doc(db, ...basePath);
    setLoading(true);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setNumero(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (err) => {
        console.error('[AppointmentTemplatesPanel] snapshot error', err);
        setError(err.message || 'snapshot_error');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [isCloudApi, numeroId, organizationId, uid]);

  // Don't render for non-Cloud-API bots (Baileys doesn't use templates).
  if (!isCloudApi) return null;

  const todasLasPlantillas = numero?.templates || {};
  const lastWebhookAt = numero?.templatesLastWebhookAt || numero?.templatesLastProvisionedAt || null;

  const handleReprovision = async () => {
    if (!botId) return;
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'provisionTemplatesForWaba');
      const res = await fn({
        botId,
        organizationId: organizationId || null,
        uid: organizationId ? null : uid,
      });
      if (res.data?.ok !== true && !Object.values(res.data?.statuses || {}).some((s) => s.id)) {
        setError(t('whatsappApi.appointmentTemplates.reprovisionError'));
      }
      // Firestore onSnapshot will refresh templates in-place.
    } catch (err) {
      console.error('[AppointmentTemplatesPanel] reprovision error', err);
      setError(err.message || t('whatsappApi.appointmentTemplates.reprovisionError'));
    } finally {
      setBusy(false);
    }
  };

  const renderStatusRow = (nombrePlantilla, langCode) => {
    const entry = (todasLasPlantillas[nombrePlantilla] || {})[langCode];
    const status = entry?.status || 'NOT_PROVISIONED';
    const meta = STATUS_META[status] || STATUS_META.NOT_PROVISIONED;
    const labelKey = `whatsappApi.appointmentTemplates.statusBadges.${
      status === 'NOT_PROVISIONED' ? 'notProvisioned' : status.toLowerCase()
    }`;
    const langLabel = t(`whatsappApi.appointmentTemplates.languages.${langCode}`);
    const reason = entry?.reason || null;

    return (
      <li key={`${nombrePlantilla}-${langCode}`} style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span aria-hidden style={{ fontSize: 14 }}>{meta.dot}</span>
        <span style={{ fontWeight: 500 }}>{langLabel}</span>
        <span style={{ color: '#52525b' }}>—</span>
        <span style={{ color: meta.color, fontWeight: 600 }}>{t(labelKey)}</span>
        {categoriaQueLimita(entry) && (
          <span
            style={{
              fontSize: 12, color: '#ea580c', marginLeft: 8, fontWeight: 600,
            }}
          >
            {t('whatsappApi.appointmentTemplates.categoriaLimita', { category: categoriaQueLimita(entry) })}
          </span>
        )}
        {reason && (
          <span
            title={reason}
            style={{
              fontSize: 12, color: '#dc2626', marginLeft: 8,
              borderBottom: '1px dotted #dc2626', cursor: 'help',
            }}
          >
            {t('whatsappApi.appointmentTemplates.rejectionReason', { reason })}
          </span>
        )}
      </li>
    );
  };

  const lastUpdatedRel = relativeTime(lastWebhookAt, lang);

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '1.5rem',
        marginTop: '1rem',
      }}
    >
      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
        📋 {t('whatsappApi.appointmentTemplates.title')}
      </h3>
      <p style={{ color: '#718096', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
        {t('whatsappApi.appointmentTemplates.description')}
      </p>

      {loading ? (
        <p style={{ color: '#718096', fontSize: 13 }}>…</p>
      ) : (
        PLANTILLAS_DE_PUX.map((nombrePlantilla) => (
          <div key={nombrePlantilla} style={{ marginBottom: 14 }}>
            <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: '#3f3f46' }}>
              {t(`whatsappApi.appointmentTemplates.nombres.${nombrePlantilla}`)}
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 14 }}>
              {IDIOMAS.map((langCode) => renderStatusRow(nombrePlantilla, langCode))}
            </ul>
          </div>
        ))
      )}

      {lastUpdatedRel && (
        <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 12px' }}>
          {t('whatsappApi.appointmentTemplates.lastUpdated', { relative: lastUpdatedRel })}
        </p>
      )}

      {error && (
        <div
          style={{
            background: '#fef2f2',
            color: '#991b1b',
            padding: '8px 12px',
            borderRadius: 4,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <button
        onClick={handleReprovision}
        disabled={busy || !botId}
        style={{
          padding: '0.5rem 1rem',
          background: busy || !botId ? '#cbd5e0' : '#667eea',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: busy || !botId ? 'not-allowed' : 'pointer',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {busy
          ? t('whatsappApi.appointmentTemplates.reprovisioning')
          : t('whatsappApi.appointmentTemplates.reprovision')}
      </button>
    </div>
  );
}
